<template>
  <div class="absolute right-0 top-full mt-2 w-96 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-50 overflow-hidden">
    <!-- Header -->
    <div class="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
      <h3 class="text-base font-semibold text-gray-900 dark:text-white">
        {{ $t('common.notifications.title') }}
      </h3>
      <div class="flex items-center space-x-3">
        <button
          v-if="unreadCount > 0"
          @click="markAllAsRead"
          class="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition-colors"
        >
          {{ $t('common.notifications.mark_all_read') }}
        </button>
        <button
          @click="$emit('close')"
          class="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <XMarkIcon class="w-5 h-5" />
        </button>
      </div>
    </div>

    <!-- Notifications list -->
    <div class="max-h-[400px] overflow-y-auto scrollbar-custom">
      <div v-if="notifications.length === 0" class="p-8 text-center">
        <div class="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
          <BellSlashIcon class="w-8 h-8 text-gray-400 dark:text-gray-500" />
        </div>
        <p class="text-gray-500 dark:text-gray-400 font-medium">
          {{ $t('common.notifications.no_notifications') }}
        </p>
      </div>

      <div v-else class="divide-y divide-gray-100 dark:divide-gray-700">
        <NotificationItem
          v-for="notification in notifications"
          :key="notification.id"
          :notification="notification"
          @click="handleNotificationClick"
          @mark-read="markAsRead"
          @remove="removeNotification"
        />
      </div>
    </div>

    <!-- Footer -->
    <div
      v-if="notifications.length > 0"
      class="px-4 py-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50"
    >
      <button
        @click="viewAllNotifications"
        class="w-full text-center text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium py-1.5 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
      >
        {{ $t('common.notifications.view_all') }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import { useAppStore } from '@/stores/app'
import NotificationItem from './NotificationItem.vue'

import {
  XMarkIcon,
  BellSlashIcon
} from '@heroicons/vue/24/outline'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()

// Helper to navigate with locale
const navigateTo = (routeName, params = {}) => {
  const locale = route.params.locale || 'en'
  router.push({ name: routeName, params: { ...params, locale } })
}

defineEmits(['close'])

const appStore = useAppStore()

const notifications = computed(() => appStore.notifications.slice(0, 10)) // Show last 10
const unreadCount = computed(() => appStore.unreadNotifications.length)

const markAllAsRead = () => {
  appStore.markAllNotificationsRead()
}

const markAsRead = (id) => {
  appStore.markNotificationRead(id)
}

const removeNotification = (id) => {
  appStore.removeNotification(id)
}

const handleNotificationClick = (notification) => {
  // Mark as read
  markAsRead(notification.id)

  // Navigate based on notification type
  if (notification.route) {
    // Add locale to existing route if it has a name
    if (notification.route.name) {
      navigateTo(notification.route.name, notification.route.params || {})
    } else {
      router.push(notification.route)
    }
  } else if (notification.campaignId) {
    navigateTo('campaigns.show', { id: notification.campaignId })
  }

  // Execute custom action if provided
  if (notification.action && typeof notification.action === 'function') {
    notification.action()
  }
}

const viewAllNotifications = () => {
  // Navigate to notifications page or show full view
  navigateTo('notifications')
}
</script>

<style scoped>
.scrollbar-custom {
  scrollbar-width: thin;
  scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
}

.scrollbar-custom::-webkit-scrollbar {
  width: 6px;
}

.scrollbar-custom::-webkit-scrollbar-track {
  background: transparent;
}

.scrollbar-custom::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.5);
  border-radius: 3px;
}

.scrollbar-custom::-webkit-scrollbar-thumb:hover {
  background-color: rgba(156, 163, 175, 0.7);
}

/* Dark mode scrollbar */
.dark .scrollbar-custom {
  scrollbar-color: rgba(75, 85, 99, 0.5) transparent;
}

.dark .scrollbar-custom::-webkit-scrollbar-thumb {
  background-color: rgba(75, 85, 99, 0.5);
}

.dark .scrollbar-custom::-webkit-scrollbar-thumb:hover {
  background-color: rgba(75, 85, 99, 0.7);
}
</style>
